// Inventory module — main page with 4 tabs.
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Boxes, ListChecks, Truck, History, RefreshCw, Loader2,
  AlertTriangle, TrendingDown, Package as PackageIcon, Coins, Archive, Layers, AlertOctagon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import { initInventory, reconcileAll, getSummary } from "@/lib/inventory";
import StockCurrentTab from "@/pages/inventory/StockCurrentTab";
import PurchaseListsTab from "@/pages/inventory/PurchaseListsTab";
import SuppliersTab from "@/pages/inventory/SuppliersTab";
import MovementsTab from "@/pages/inventory/MovementsTab";
import SnapshotsTab from "@/pages/inventory/SnapshotsTab";
import BundlesTab from "@/pages/inventory/BundlesTab";
import DefectiveTab from "@/pages/inventory/DefectiveTab";

export default function InventoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "stock");
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSummary = useCallback(async () => {
    try { setSummary(await getSummary()); } catch {}
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const onTab = (v) => {
    setTab(v);
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.set("tab", v);
      return next;
    });
  };

  const runInit = async () => {
    if (!window.confirm("Inițializează stocurile din Shopify? (Sigur la re-run — nu se va modifica stocul existent.)")) return;
    setBusy(true);
    const tid = toast.loading("Inițializez...");
    try {
      const r = await initInventory();
      toast.success(`${r.items_inserted} create · ${r.items_updated} actualizate · ${r.orders_stamped} comenzi marcate`, { id: tid });
      await loadSummary();
    } catch (e) {
      toast.error("Eroare la init", { id: tid });
    } finally { setBusy(false); }
  };

  const runReconcile = async () => {
    setBusy(true);
    const tid = toast.loading("Reconciliez comenzile...");
    try {
      const r = await reconcileAll();
      toast.success(`${r.reconciled} comenzi reconciliate`, { id: tid });
      await loadSummary();
    } catch (e) {
      toast.error("Eroare", { id: tid });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Boxes className="h-6 w-6 text-primary" />
            <h1 className="font-display text-2xl font-bold tracking-tight">Stocuri</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Managementul stocurilor reale din depozit · independent de stocul Shopify.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={runReconcile} disabled={busy} data-testid="inventory-reconcile-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Reconciliază
          </Button>
          <Button variant="ghost" size="sm" onClick={runInit} disabled={busy} data-testid="inventory-init-btn" title="Rulează init (idempotent)">
            Init
          </Button>
        </div>
      </div>

      {/* ANALYTICS */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="inventory-summary">
        <SummaryCard
          label="Produse active"
          value={summary?.total_items ?? "—"}
          icon={PackageIcon}
          color="text-foreground"
          testid="kpi-total-items"
        />
        <SummaryCard
          label="Fără stoc fizic"
          value={summary?.out_of_stock_physical ?? "—"}
          icon={AlertTriangle}
          color="text-red-500"
          testid="kpi-out-of-stock"
        />
        <SummaryCard
          label="Necesită aprovizionare"
          value={summary?.needs_supply ?? "—"}
          icon={ListChecks}
          color="text-amber-500"
          testid="kpi-needs-supply"
        />
        <SummaryCard
          label="Total unități necesare"
          value={summary?.total_units_needed ?? "—"}
          icon={Boxes}
          color="text-orange-500"
          testid="kpi-units-needed"
        />
        <SummaryCard
          label="Valoare aprovizionare"
          value={summary ? `${parseFloat(summary.estimated_supply_value).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON` : "—"}
          icon={Coins}
          color="text-emerald-500"
          testid="kpi-supply-value"
        />
        <SummaryCard
          label="Supravândute"
          value={summary?.oversold ?? "—"}
          icon={TrendingDown}
          color="text-purple-500"
          testid="kpi-oversold"
        />
      </div>

      {/* TABS */}
      <Tabs value={tab} onValueChange={onTab} className="w-full">
        <TabsList className="grid grid-cols-7 max-w-5xl">
          <TabsTrigger value="stock" data-testid="tab-stock">
            <Boxes className="h-3.5 w-3.5 mr-1.5" /> Stoc curent
          </TabsTrigger>
          <TabsTrigger value="purchases" data-testid="tab-purchases">
            <ListChecks className="h-3.5 w-3.5 mr-1.5" /> Aprovizionare
          </TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">
            <Truck className="h-3.5 w-3.5 mr-1.5" /> Furnizori
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="h-3.5 w-3.5 mr-1.5" /> Istoric
          </TabsTrigger>
          <TabsTrigger value="snapshots" data-testid="tab-snapshots">
            <Archive className="h-3.5 w-3.5 mr-1.5" /> Arhivă lunar
          </TabsTrigger>
          <TabsTrigger value="bundles" data-testid="tab-bundles">
            <Layers className="h-3.5 w-3.5 mr-1.5" /> Bundle-uri
          </TabsTrigger>
          <TabsTrigger value="defective" data-testid="tab-defective">
            <AlertOctagon className="h-3.5 w-3.5 mr-1.5" /> Produse defecte
          </TabsTrigger>
        </TabsList>

        <TabsContent value="stock" className="mt-4">
          <StockCurrentTab onChanged={loadSummary} />
        </TabsContent>
        <TabsContent value="purchases" className="mt-4">
          <PurchaseListsTab onChanged={loadSummary} />
        </TabsContent>
        <TabsContent value="suppliers" className="mt-4">
          <SuppliersTab />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <MovementsTab />
        </TabsContent>
        <TabsContent value="snapshots" className="mt-4">
          <SnapshotsTab />
        </TabsContent>
        <TabsContent value="bundles" className="mt-4">
          <BundlesTab />
        </TabsContent>
        <TabsContent value="defective" className="mt-4">
          <DefectiveTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function SummaryCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          {label}
        </div>
        <Icon className={`h-3.5 w-3.5 ${color}`} />
      </div>
      <div className={`text-2xl font-display font-bold mt-1 tabular-nums ${color}`}>
        {value}
      </div>
    </div>
  );
}
